<template>
    <!-- ВКЛАДКА «ФАЙЛЫ». Сверху — как добавить, ниже — что уже есть. Порядок именно такой:
         на телефон приходят, чтобы снять кадр, а не чтобы листать снятое, и кнопка съёмки
         не должна оказываться под списком из двадцати фотографий. -->
    <div class="inventory-files">
        <div class="inventory-files__loader" v-if="isLoading">
            <VSpinner></VSpinner>
        </div>

        <template v-else>
            <p class="inventory-files__error" v-if="error">{{ error }}</p>

            <!-- ДОБАВЛЕНИЕ. Показывается только тому, кто вправе править эту позицию:
                 право раздела плюс город позиции — та же проверка, что у кнопки
                 «Редактировать». Скрытие здесь — удобство; отказывает всё равно сервер. -->
            <section class="inventory-files__add" v-if="canEdit">
                <!-- Черновика нет — показываем кнопки выбора. Два поля, а не одно:
                     accept у них разный, и на телефоне от этого зависит, что откроется —
                     камера с галереей или файловый менеджер. -->
                <div class="inventory-files__pick" v-if="!draft">
                    <VButton
                        type="button"
                        class="button_green inventory-files__pick-button"
                        :disabled="isFull"
                        @click="choosePhoto"
                    >
                        <span>Добавить фото</span>
                    </VButton>

                    <!-- Съёмка отдельной кнопкой и только там, где браузер понимает
                         capture. Вешать capture на кнопку «Добавить фото» нельзя:
                         на телефоне это отрезает галерею, и уже снятый кадр
                         приложить становится нечем. -->
                    <VButton
                        type="button"
                        class="button_green-light inventory-files__pick-button"
                        v-if="isCaptureSupported"
                        :disabled="isFull"
                        @click="chooseCapture"
                    >
                        <span>Снять камерой</span>
                    </VButton>

                    <VButton
                        type="button"
                        class="button_green-light inventory-files__pick-button"
                        :disabled="isFull"
                        @click="chooseDocument"
                    >
                        <span>Документ</span>
                    </VButton>
                </div>

                <p class="inventory-files__limit" v-if="isFull">
                    К предмету уже прикреплено {{ limits.maxFilesPerItem }} файлов — больше нельзя.
                </p>

                <!-- Поля выбора спрятаны: их внешний вид не поддаётся оформлению,
                     и во всём приложении их открывают кнопкой (см. FileUploader). -->
                <input
                    class="inventory-files__input"
                    ref="photoInput"
                    type="file"
                    :accept="photoAccept"
                    @change="stageFromInput"
                />

                <input
                    class="inventory-files__input"
                    ref="captureInput"
                    type="file"
                    :accept="photoAccept"
                    capture="environment"
                    @change="stageFromInput"
                />

                <input
                    class="inventory-files__input"
                    ref="documentInput"
                    type="file"
                    :accept="documentAccept"
                    @change="stageFromInput"
                />

                <!-- ЧЕРНОВИК: файл выбран, но ещё не отправлен. Так выглядит «предпросмотр
                     перед загрузкой» — кадр виден целиком, и если он смазан, его убирают,
                     не потратив ни байта мобильного трафика. -->
                <div class="inventory-files__draft" v-if="draft">
                    <div class="inventory-files__draft-head">
                        <div class="inventory-files__draft-preview" v-if="draft.dataUrl">
                            <img class="inventory-files__draft-image" :src="draft.dataUrl" :alt="draft.name" />
                        </div>

                        <div class="inventory-files__draft-badge" v-else>
                            {{ draftExtension }}
                        </div>

                        <div class="inventory-files__draft-box">
                            <p class="inventory-files__draft-name" :title="draft.name">{{ draft.name }}</p>
                            <p class="inventory-files__draft-size">{{ draftSize }}</p>
                        </div>

                        <button
                            type="button"
                            class="inventory-files__draft-drop"
                            title="Убрать файл"
                            :disabled="isUploading"
                            @click="discardDraft"
                        >
                            <TimesSvg></TimesSvg>
                        </button>
                    </div>

                    <p class="inventory-files__draft-error" v-if="draft.error">{{ draft.error }}</p>

                    <template v-else>
                        <label class="inventory-files__label" for="inventory-file-category">Категория</label>

                        <VSelect
                            id="inventory-file-category"
                            name="file-category"
                            label="label"
                            class="inventory-files__control control"
                            placeholder="Категория"
                            :clearable="false"
                            :searchable="false"
                            :disabled="isUploading"
                            :options="categoryOptions"
                            :reduce="(option) => option.value"
                            v-model="draft.category"
                        />

                        <label class="inventory-files__label" for="inventory-file-comment">Комментарий</label>

                        <input
                            id="inventory-file-comment"
                            class="inventory-files__control control"
                            type="text"
                            placeholder="Чем этот кадр важен"
                            :maxlength="limits.maxCommentLength"
                            :disabled="isUploading"
                            v-model="draft.comment"
                        />

                        <!-- Полоса отправки: на телефоне фотография уходит десятки секунд,
                             и без неё загрузка неотличима от зависшего приложения. -->
                        <div class="inventory-files__progress" v-if="isUploading">
                            <div class="inventory-files__progress-bar" :style="{ width: progress + '%' }"></div>
                        </div>

                        <div class="inventory-files__draft-actions">
                            <VButton type="button" class="button_green" :disabled="isUploading" @click="upload">
                                {{ isUploading ? 'Загружаем…' : 'Загрузить' }}
                            </VButton>

                            <VButton
                                type="button"
                                class="button_green-light"
                                :disabled="isUploading"
                                @click="discardDraft"
                            >
                                Отмена
                            </VButton>
                        </div>
                    </template>
                </div>
            </section>

            <!-- Не при ошибке загрузки: «файлов нет» рядом с «не удалось загрузить» — неправда. -->
            <p class="inventory-files__empty" v-if="!attachments.length && !error">Файлов пока нет.</p>

            <!-- ФОТОГРАФИИ — плиткой. Их смотрят, а не читают: имя файла с телефона
                 (IMG_0421.HEIC) не говорит ничего, а сам кадр говорит всё. -->
            <section class="inventory-files__group" v-if="photos.length">
                <h3 class="inventory-files__group-title">Фотографии · {{ photos.length }}</h3>

                <ul class="inventory-files__grid">
                    <li class="inventory-files__photo" v-for="photo in photos" :key="photo.id">
                        <!-- Кадр приходит запросом с токеном и показывается через object URL:
                             открытой ссылки у вложений оборудования нет, а <img src>
                             заголовок авторизации не отправляет. Пока кадр едет, на его
                             месте рамка-заглушка, а не битая картинка. -->
                        <button
                            type="button"
                            class="inventory-files__photo-frame"
                            :class="{
                                'inventory-files__photo-frame_pending': !blobs[photo.id],
                                'inventory-files__photo-frame_primary': photo.is_primary,
                            }"
                            :title="photo.name"
                            :disabled="!blobs[photo.id]"
                            @click="openImage(photo)"
                        >
                            <img
                                class="inventory-files__photo-image"
                                v-if="blobs[photo.id]"
                                :src="blobs[photo.id]"
                                :alt="photo.name"
                            />

                            <!-- Метка ПОВЕРХ кадра, а не подписью под ним: плитка
                                 со стороной в сотню точек читается целиком, и метка рядом
                                 с ней потерялась бы среди категории и комментария. -->
                            <span class="inventory-files__photo-badge" v-if="photo.is_primary">Главное фото</span>
                        </button>

                        <p class="inventory-files__chip">{{ photo.category_title }}</p>

                        <p class="inventory-files__comment" v-if="photo.comment">{{ photo.comment }}</p>

                        <p class="inventory-files__meta">{{ meta(photo) }}</p>

                        <div class="inventory-files__photo-actions">
                            <!-- Кнопка, а не ссылка: href вёл бы на адрес, который отдаёт
                                 файл без токена, а такого адреса у вложений оборудования
                                 нет. Сохранение идёт из уже полученного содержимого. -->
                            <button
                                type="button"
                                class="inventory-files__action inventory-files__action_icon"
                                title="Скачать фото"
                                aria-label="Скачать фото"
                                :disabled="isSaving === photo.id"
                                @click="save(photo)"
                            >
                                <DownloadIcon :size="18"></DownloadIcon>
                            </button>

                            <!-- Только у фотографий: в шапке карточки показывается кадр,
                                 и документ там показать нечем. У документов этого действия
                                 нет вовсе — не «есть, но отказывает».

                                 Обычная кнопка, видимая всегда. Появление по наведению
                                 на телефоне означало бы «нет вовсе»: наводить там нечем. -->
                            <button
                                type="button"
                                class="inventory-files__action inventory-files__action_icon inventory-files__action_primary"
                                :class="{ 'inventory-files__action_primary-on': photo.is_primary }"
                                :title="photo.is_primary ? 'Снять главное' : 'Сделать главным'"
                                :aria-label="photo.is_primary ? 'Снять главное' : 'Сделать главным'"
                                :aria-pressed="photo.is_primary ? 'true' : 'false'"
                                v-if="canEdit"
                                :disabled="isPrimarySaving === photo.id"
                                @click="togglePrimary(photo)"
                            >
                                <StarIcon :size="18"></StarIcon>
                            </button>

                            <button
                                type="button"
                                class="inventory-files__action inventory-files__action_icon inventory-files__action_danger"
                                title="Удалить фото"
                                aria-label="Удалить фото"
                                v-if="canEdit"
                                :disabled="isRemoving === photo.id"
                                @click="remove(photo)"
                            >
                                <DeleteIcon :size="18"></DeleteIcon>
                            </button>
                        </div>
                    </li>
                </ul>
            </section>

            <!-- ДОКУМЕНТЫ — строками. У них смысл в имени и типе, а не в виде. -->
            <section class="inventory-files__group" v-if="documents.length">
                <h3 class="inventory-files__group-title">Документы · {{ documents.length }}</h3>

                <ul class="inventory-files__list">
                    <li class="inventory-files__document" v-for="document in documents" :key="document.id">
                        <!-- Тоже кнопка, а не ссылка, и по той же причине: адреса, который
                             отдаёт файл сам по себе, у вложений оборудования не существует.
                             Документы, в отличие от фотографий, заранее не забираются —
                             показывать у них нечего, а весить они могут мегабайты. -->
                        <button
                            type="button"
                            class="inventory-files__document-link"
                            :disabled="isSaving === document.id"
                            @click="save(document)"
                        >
                            <span class="inventory-files__document-badge">{{ extension(document) }}</span>

                            <span class="inventory-files__document-box">
                                <span class="inventory-files__document-name">{{ document.name }}</span>
                                <span class="inventory-files__document-size">{{ size(document) }}</span>
                            </span>
                        </button>

                        <p class="inventory-files__chip">{{ document.category_title }}</p>

                        <p class="inventory-files__comment" v-if="document.comment">{{ document.comment }}</p>

                        <p class="inventory-files__meta">{{ meta(document) }}</p>

                        <div class="inventory-files__document-actions" v-if="canEdit">
                            <button
                                type="button"
                                class="inventory-files__action inventory-files__action_icon inventory-files__action_danger"
                                title="Удалить документ"
                                aria-label="Удалить документ"
                                :disabled="isRemoving === document.id"
                                @click="remove(document)"
                            >
                                <DeleteIcon :size="18"></DeleteIcon>
                            </button>
                        </div>
                    </li>
                </ul>
            </section>
        </template>
    </div>
</template>
<script>
// svg
import TimesSvg from '@/images/times.svg';
import DownloadIcon from 'vue-material-design-icons/Download.vue';
import StarIcon from 'vue-material-design-icons/Star.vue';
import DeleteIcon from 'vue-material-design-icons/Delete.vue';

// Components
import VButton from '../../../components/VButton/VButton.vue';
import VSelect from '../../../components/VSelect/VSelect.vue';
import VSpinner from '../../../components/VProgress/VSpinner.vue';

// Other
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop } from 'vue-property-decorator';
import { errorMessage } from '../../../../js/error';
import ModalNames from '../../../../js/enums/ModalNames';
import InventoryFileCategory from '../../../../js/enums/InventoryFileCategory';
import {
    DOCUMENT_ACCEPT,
    PHOTO_ACCEPT,
    canBePrimaryPhoto,
    fileCategoryOptions,
    fileExtension,
    formatFileSize,
    fileErrorTitle,
    normalizeFileLimits,
    primaryPhotoOf,
    splitInventoryFiles,
    validateInventoryFile,
    withPrimaryPhoto,
} from '../../../../js/inventoryFiles';
import {
    attachInventoryItemFile,
    deleteInventoryItemFile,
    downloadInventoryItemFile,
    getInventoryItemFiles,
    setInventoryItemFilePrimary,
    uploadFileToStorage,
} from '../../../../js/inventory-api';

/**
 * Вкладка «Файлы» карточки оборудования: фотографии и документы конкретного экземпляра.
 *
 * ЗАГРУЗКА В ДВА ШАГА, И ЭТО ВИДНО ПОЛЬЗОВАТЕЛЮ КАК ОДИН. Сначала файл уходит в общее
 * хранилище приложения (POST /v1/file/upload — тот же, которым пользуются вложения заявок),
 * затем прикрепляется к предмету вместе с категорией и подписью. Разбиение не прихоть
 * интерфейса: механизм записи в хранилище во всём приложении один, и заводить второй
 * ради оборудования значило бы развести права, пути и чистку.
 *
 * ЧЕРНОВИК ПЕРЕД ОТПРАВКОЙ. Выбранный файл сначала показывается, и только по нажатию
 * «Загрузить» уходит на сервер. Ради телефона: смазанный кадр видно сразу, и убрать его
 * дешевле, чем отправить 15 МБ по мобильной сети и удалить уже загруженное.
 *
 * ПРАВА НЕ ВЫЧИСЛЯЮТСЯ ЗДЕСЬ. canEdit приходит от карточки — это её $_canEditInventoryItem,
 * то есть право раздела вместе с городом позиции. Ни ролей, ни групп компонент не знает.
 */
@Component({
    components: {
        TimesSvg,
        DownloadIcon,
        StarIcon,
        DeleteIcon,
        VButton,
        VSelect,
        VSpinner,
    },
})
class VInventoryItemFiles extends Vue {
    @Prop({ type: Number, required: true })
    itemId;

    /**
     * Можно ли править ИМЕННО ЭТУ позицию. Право раздела без города позиции здесь
     * не годится: наблюдатель из городской группы видит всю технику, а правит только свою.
     */
    @Prop({ type: Boolean, default: false })
    canEdit;

    attachments = [];
    limits = normalizeFileLimits(null);

    isLoading = false;
    isUploading = false;
    isRemoving = null;
    isSaving = null;
    isPrimarySaving = null;
    progress = 0;
    error = '';

    // Полученное содержимое вложений: номер вложения → object URL. Открытых адресов
    // у файлов оборудования нет, поэтому и кадр в плитке, и сохранение на диск берут
    // байты отсюда. Отпускаются в releaseBlobs().
    blobs = {};

    // Выбранный, но ещё не отправленный файл. Ровно один: категория и подпись пишутся
    // к каждому вложению свои, а форма на пачку файлов сразу означала бы либо одну
    // категорию на всех, либо столько форм, сколько кадров.
    draft = null;

    get photos() {
        return splitInventoryFiles(this.attachments).photos;
    }

    get documents() {
        return splitInventoryFiles(this.attachments).documents;
    }

    /**
     * Главное фото предмета или null. Признак приходит с бэкенда у самого вложения —
     * вычислять его здесь («первое фото списка») значило бы завести второе правило.
     */
    get primaryPhoto() {
        return primaryPhotoOf(this.attachments);
    }

    get categoryOptions() {
        return fileCategoryOptions();
    }

    get photoAccept() {
        return PHOTO_ACCEPT;
    }

    get documentAccept() {
        return DOCUMENT_ACCEPT;
    }

    /**
     * Понимает ли браузер capture. На настольном браузере атрибут игнорируется, и кнопка
     * «Снять камерой» открывала бы там обычный выбор файла — то есть врала бы.
     */
    get isCaptureSupported() {
        return typeof document !== 'undefined' && 'capture' in document.createElement('input');
    }

    get isFull() {
        const max = this.limits.maxFilesPerItem;

        return max !== null && this.attachments.length >= max;
    }

    get draftExtension() {
        return this.draft ? fileExtension(this.draft.name).toUpperCase() : '';
    }

    get draftSize() {
        return this.draft ? formatFileSize(this.draft.size) : '';
    }

    extension(attachment) {
        return fileExtension(attachment.name).toUpperCase();
    }

    size(attachment) {
        return formatFileSize(attachment.size);
    }

    /**
     * Строка «когда и кто». Дата со временем, а не одна дата: за приёмку снимают пачку
     * кадров в один день, и без времени их порядок необъясним.
     *
     * @param {Object} attachment
     * @returns {string}
     */
    meta(attachment) {
        const when = attachment.uploaded_at ? this.$_toLocalDateShort(attachment.uploaded_at) : '';
        const who = attachment.uploader ? attachment.uploader.display_name : 'автор неизвестен';

        return when ? `${when} · ${who}` : who;
    }

    /**
     * Открыть кадр во весь экран. Только когда содержимое уже получено: показывать
     * лайтбокс с пустым адресом значит открывать чёрный прямоугольник.
     *
     * @param {Object} photo
     */
    openImage(photo) {
        const url = this.blobs[photo.id];

        if (url) {
            this.$bus.emit('ImagePopup:Show', url);
        }
    }

    /**
     * Забрать содержимое вложения и запомнить object URL.
     *
     * Повторно не запрашивается: карточку открывают и листают, и каждый возврат
     * на вкладку иначе тянул бы те же мегабайты заново.
     *
     * @param {Object} attachment
     * @returns {Promise<string>} Пустая строка, если получить не удалось.
     */
    async loadBlob(attachment) {
        if (this.blobs[attachment.id]) {
            return this.blobs[attachment.id];
        }

        const response = await downloadInventoryItemFile(this.itemId, attachment.id);
        const url = URL.createObjectURL(response.data);

        // Vue 2 не видит новые ключи объекта — без $set кадр приедет, а плитка
        // останется заглушкой.
        this.$set(this.blobs, attachment.id, url);

        return url;
    }

    /**
     * Показать фотографии. Документы сюда не попадают намеренно: у них нет превью,
     * а весить они могут мегабайты — их содержимое забирается только по нажатию.
     */
    async loadPhotoPreviews() {
        await Promise.all(
            this.photos.map((attachment) =>
                this.loadBlob(attachment).catch(() => {
                    // Один не открывшийся кадр не должен гасить остальные и не должен
                    // поднимать модалку: на его месте остаётся пустая рамка.
                    return '';
                }),
            ),
        );
    }

    /**
     * Сохранить вложение на диск пользователя.
     *
     * Своим нажатием, а не ссылкой: адреса, который отдаёт файл без токена, у вложений
     * оборудования не существует — содержимое приходит запросом, и уже из него делается
     * временная ссылка, по которой браузер и сохраняет файл под настоящим именем.
     *
     * @param {Object} attachment
     */
    async save(attachment) {
        if (this.isSaving) {
            return;
        }

        this.isSaving = attachment.id;

        try {
            const url = await this.loadBlob(attachment);
            const link = document.createElement('a');

            link.href = url;
            link.download = attachment.name;
            // В DOM, а не просто click(): Firefox не сработает на узле вне документа.
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (requestError) {
            this.$showModal(ModalNames.ERROR, { errorMessage: errorMessage(requestError) });
        } finally {
            this.isSaving = null;
        }
    }

    /**
     * Отпустить временные ссылки.
     *
     * Object URL держит содержимое файла в памяти вкладки, пока его не отозвали, и живёт
     * до перезагрузки страницы. Карточка открывается десятки раз за смену, и без этого
     * каждая пачка фотографий оставалась бы висеть.
     */
    releaseBlobs(keepIds = null) {
        const keep = Array.isArray(keepIds) ? keepIds.map((value) => String(value)) : [];
        const kept = {};

        Object.keys(this.blobs).forEach((id) => {
            if (keep.indexOf(id) !== -1) {
                kept[id] = this.blobs[id];

                return;
            }

            URL.revokeObjectURL(this.blobs[id]);
        });

        this.blobs = kept;
    }

    /**
     * Сообщить карточке, какое фото сейчас главное.
     *
     * СОДЕРЖИМОЕ ОТДАЁТСЯ ГОТОВЫМ, а не номером вложения, и это главная причина, по которой
     * шапку кормит вкладка, а не карточка сама. У файлов оборудования нет открытого адреса:
     * кадр приезжает запросом с токеном и живёт как object URL. Запроси его карточка
     * отдельно — те же байты приехали бы дважды за одно открытие.
     *
     * Пустая ссылка равнозначна её отсутствию: кадр ещё едет или не приехал вовсе,
     * и шапке в обоих случаях показывать нечего — пусть берёт следующее в своей цепочке.
     */
    emitPrimary() {
        const primary = this.primaryPhoto;
        const url = primary ? this.blobs[primary.id] : '';

        this.$emit('primary', url ? { id: primary.id, url } : null);
    }

    choosePhoto() {
        this.openPicker(this.$refs.photoInput);
    }

    chooseCapture() {
        this.openPicker(this.$refs.captureInput);
    }

    chooseDocument() {
        this.openPicker(this.$refs.documentInput);
    }

    openPicker(input) {
        if (input) {
            input.click();
        }
    }

    /**
     * Выбранный файл становится черновиком.
     *
     * Значение поля сбрасывается сразу: без этого повторный выбор того же файла (снял,
     * передумал, снял снова тем же именем) не даёт события change вовсе.
     *
     * @param {Event} event
     */
    stageFromInput(event) {
        const input = event.target;
        const file = input && input.files ? input.files[0] : null;

        if (input) {
            input.value = '';
        }

        if (!file) {
            return;
        }

        this.stage(file);
    }

    /**
     * Проверить файл и показать его. Проверка здесь не защита, а вежливость: те же правила
     * стоят на бэкенде, и смысл клиентской — не гнать файл по сети ради отказа.
     *
     * @param {File} file
     */
    stage(file) {
        const failure = validateInventoryFile(file, this.limits);

        this.draft = {
            file,
            name: file.name,
            size: file.size,
            type: file.type,
            dataUrl: '',
            category: InventoryFileCategory.GENERAL,
            comment: '',
            error: failure ? fileErrorTitle(failure) : '',
        };

        if (!failure && /^image\//.test(String(file.type))) {
            this.readPreview(file);
        }
    }

    /**
     * Кадр для предпросмотра. Только для картинок: у документа показывать нечего,
     * а читать мегабайты в память ради этого не за чем.
     *
     * @param {File} file
     */
    readPreview(file) {
        const reader = new FileReader();

        reader.onload = (event) => {
            // Черновик мог смениться, пока файл читался: показывать старый кадр
            // под новым именем хуже, чем не показывать ничего.
            if (this.draft && this.draft.file === file) {
                this.draft.dataUrl = event.target.result;
            }
        };

        reader.readAsDataURL(file);
    }

    discardDraft() {
        this.draft = null;
        this.progress = 0;
    }

    /**
     * Отправить черновик: сначала файл в хранилище, потом связь с предметом.
     *
     * Если первый запрос прошёл, а второй нет, в files остаётся строка, ни к чему
     * не привязанная. Это осознанный размен: удалять её отсюда значит заводить
     * в интерфейсе единственное место, которое стирает чужие файлы.
     */
    async upload() {
        if (!this.draft || this.draft.error || this.isUploading) {
            return;
        }

        this.isUploading = true;
        this.progress = 0;

        try {
            const uploaded = await uploadFileToStorage(this.draft.file, (value) => {
                this.progress = value;
            });

            const file = Array.isArray(uploaded.data) ? uploaded.data[0] : null;

            if (!file || !file.id) {
                throw new Error('Файл не загрузился. Попробуйте ещё раз.');
            }

            await attachInventoryItemFile(this.itemId, {
                fileId: file.id,
                category: this.draft.category,
                comment: this.draft.comment,
            });

            this.discardDraft();
            await this.fetch();
            this.$emit('changed');
        } catch (requestError) {
            this.$showModal(ModalNames.ERROR, { errorMessage: errorMessage(requestError) });
        } finally {
            this.isUploading = false;
        }
    }

    /**
     * Назначить фотографию главной или снять признак.
     *
     * СПИСОК НЕ ПЕРЕЧИТЫВАЕТСЯ. Сервер меняет признак ровно у двух вложений — нового
     * главного и прежнего, — и то же самое делает здесь withPrimaryPhoto(). Запрос за
     * списком ради двух булевых значений заодно отпустил бы и заново забрал содержимое
     * всех кадров: на телефоне это мегабайты за нажатие.
     *
     * Проверки те же, что у сервера, и по той же причине, что у остальных действий вкладки:
     * скрытая кнопка защищает от случайного нажатия, а это — от вызова мимо неё.
     *
     * @param {Object} photo
     */
    async togglePrimary(photo) {
        if (!this.canEdit || this.isPrimarySaving || !canBePrimaryPhoto(photo)) {
            return;
        }

        const next = !photo.is_primary;

        this.isPrimarySaving = photo.id;

        try {
            await setInventoryItemFilePrimary(this.itemId, photo.id, next);

            this.attachments = withPrimaryPhoto(this.attachments, next ? photo.id : null);

            // Шапке — новое фото, списку слева — что карточка изменилась. Порядок важен
            // только тем, что оба должны случиться после успешного ответа, а не до него.
            this.emitPrimary();
            this.$emit('changed');
        } catch (requestError) {
            this.$showModal(ModalNames.ERROR, { errorMessage: errorMessage(requestError) });
        } finally {
            this.isPrimarySaving = null;
        }
    }

    /**
     * Снять вложение. Подтверждения здесь нет намеренно: удаляется вложение, а не предмет,
     * и снять неудачный кадр — обычное действие, а не опасное.
     *
     * Удаление главного фото ничего особенного не требует: признак уходит вместе со строкой
     * связи, и следующий emitPrimary() скажет шапке, что главного фото больше нет.
     * Назначать взамен другое здесь никто не должен — это решение человека, не программы.
     *
     * @param {Object} attachment
     */
    async remove(attachment) {
        if (!this.canEdit || this.isRemoving) {
            return;
        }

        this.isRemoving = attachment.id;

        try {
            await deleteInventoryItemFile(this.itemId, attachment.id);
            await this.fetch();
            this.$emit('changed');
        } catch (requestError) {
            this.$showModal(ModalNames.ERROR, { errorMessage: errorMessage(requestError) });
        } finally {
            this.isRemoving = null;
        }
    }

    async fetch() {
        this.error = '';

        let next = [];
        let limits = this.limits;

        try {
            const response = await getInventoryItemFiles(this.itemId);

            next = Array.isArray(response.data.data) ? response.data.data : [];
            limits = normalizeFileLimits(response.data.limits);
        } catch (requestError) {
            this.error = errorMessage(requestError);
        }

        this.attachments = next;
        this.limits = limits;

        // ОТПУСКАЕТСЯ ТОЛЬКО ТО, ЧЕГО В НОВОМ СОСТАВЕ БОЛЬШЕ НЕТ. Вложение неизменяемо,
        // и его номер не достаётся потом другому файлу, поэтому уже полученный кадр
        // переживает перечитывание списка. Отпускать всё подряд значило бы после каждого
        // удаления тянуть заново все остальные кадры — по мобильной сети это заметно.
        // Удалённое при этом уходит обязательно: открываемым из памяти вкладки оно
        // остаться не должно.
        this.releaseBlobs(this.attachments.map((attachment) => attachment.id));

        if (this.error) {
            this.emitPrimary();

            return;
        }

        await this.loadPhotoPreviews();

        this.emitPrimary();
    }

    beforeDestroy() {
        // Сначала шапке, потом памяти: сказать «фото больше нет» после того, как ссылка
        // уже отозвана, — значит на мгновение оставить в шапке недействительный адрес.
        this.$emit('primary', null);
        this.releaseBlobs();
    }

    async created() {
        this.isLoading = true;

        try {
            await this.fetch();
        } finally {
            this.isLoading = false;
        }
    }
}

export default VInventoryItemFiles;
</script>
<style lang="scss">
// Вкладка «Файлы».
//
// СНАЧАЛА УЗКИЙ ЭКРАН. Раздел открывают с телефона у стеллажа со сканером в руке, и
// раскладка рассчитана на такую ширину: одна колонка, кнопки во всю строку, плитки
// подстраиваются сеткой. Широкой панели от этого ничего не теряется — колонок становится
// больше, а правила остаются те же.
//
// Медиазапросов здесь почти нет и быть не должно: ширина панели задана в процентах
// рабочей области (28…50%), а не окна, и по ширине окна о ней судить нельзя.
// Раскладку держат auto-fill и flex-wrap.
.inventory-files {
    --files-line: #e8eaed;
    --files-muted: #5f6368;
    --files-faint: #9aa0a6;
    --files-surface: #f6f7f9;

    padding-top: 4px;

    &__loader {
        display: flex;
        justify-content: center;
        padding: 32px 0;
    }

    &__error {
        margin: 12px 0;
        color: var(--color-red);
        font-size: 0.8125rem;
    }

    &__empty,
    &__limit {
        margin: 12px 0;
        color: var(--files-muted);
        font-size: 0.8125rem;
    }

    // Поле выбора файла не оформляется ни в одном браузере одинаково — во всём приложении
    // его открывают кнопкой (см. FileUploader).
    &__input {
        display: none;
    }

    &__add {
        margin-bottom: 18px;
    }

    // Кнопки выбора. Перенос, а не медиазапрос: на узкой панели они встают столбиком
    // сами, и каждая занимает строку целиком.
    &__pick {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
    }

    &__pick-button {
        flex: 1 1 140px;
        // 40 точек — нижняя граница уверенного попадания пальцем. На мышь это не влияет.
        min-height: 40px;
        justify-content: center;
    }

    // ЧЕРНОВИК. Подложка собирает его в один блок: это временное состояние, и оно должно
    // читаться как «ещё не отправлено», а не как первая карточка списка.
    &__draft {
        margin-top: 12px;
        padding: 12px;
        border-radius: 8px;
        background: var(--files-surface);
    }

    &__draft-head {
        display: flex;
        align-items: center;
        gap: 10px;
    }

    &__draft-preview {
        flex: 0 0 auto;
        width: 64px;
        height: 64px;
        overflow: hidden;
        border-radius: 6px;
        background: #fff;
    }

    &__draft-image {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
    }

    &__draft-badge {
        flex: 0 0 auto;
        display: flex;
        width: 64px;
        height: 64px;
        align-items: center;
        justify-content: center;
        border-radius: 6px;
        background: #fff;
        color: #f1905b;
        font: 400 0.75rem var(--font-family);
        text-transform: uppercase;
    }

    // min-width: 0 обязателен — без него длинное имя файла растягивает строку
    // и выдавливает кнопку сброса за край панели.
    &__draft-box {
        flex: 1 1 auto;
        min-width: 0;
    }

    &__draft-name {
        margin: 0;
        overflow: hidden;
        color: #202124;
        font-size: 0.8125rem;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    &__draft-size {
        margin: 2px 0 0;
        color: var(--files-faint);
        font-size: 0.75rem;
    }

    &__draft-drop {
        flex: 0 0 auto;
        display: flex;
        width: 32px;
        height: 32px;
        align-items: center;
        justify-content: center;
        padding: 0;
        border: 0;
        border-radius: 50%;
        background: transparent;
        color: var(--color-red);
        cursor: pointer;

        svg {
            width: 12px;
            height: 12px;
            fill: currentColor;
        }

        &:hover {
            background: rgba(250, 0, 0, 0.08);
        }
    }

    &__draft-error {
        margin: 10px 0 0;
        color: var(--color-red);
        font-size: 0.8125rem;
    }

    &__draft-actions {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        margin-top: 12px;

        .button {
            flex: 1 1 120px;
            min-height: 40px;
            justify-content: center;
        }
    }

    &__label {
        display: block;
        margin: 12px 0 4px;
        color: var(--files-muted);
        font-size: 0.75rem;
    }

    &__control {
        width: 100%;
        box-sizing: border-box;
    }

    &__progress {
        height: 4px;
        margin-top: 12px;
        overflow: hidden;
        border-radius: 2px;
        background: #fff;
    }

    &__progress-bar {
        height: 100%;
        background: $green;
        transition: width 0.2s ease;
    }

    &__group {
        margin-top: 18px;
    }

    &__group-title {
        margin: 0 0 8px;
        color: var(--files-muted);
        font-size: 0.75rem;
        font-weight: 500;
        text-transform: uppercase;
        letter-spacing: 0.04em;
    }

    // Плитка фотографий. auto-fill с минимумом в 104 точки: на телефоне помещаются три
    // столбца, на широкой панели — сколько войдёт. Явного числа колонок нет намеренно,
    // ширина панели меняется перетаскиванием.
    &__grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(min(112px, 100%), 1fr));
        gap: 12px;
        margin: 0;
        padding: 0;
        list-style: none;
    }

    &__photo {
        min-width: 0;
    }

    &__photo-frame {
        display: block;
        width: 100%;
        padding: 0;
        overflow: hidden;
        border: 1px solid var(--files-line);
        border-radius: 8px;
        background: var(--files-surface);
        cursor: zoom-in;

        // Квадрат: у кадров с телефона разная ориентация, и без него плитка скачет.
        // max-width страхует от вылезания за колонку на самых узких экранах.
        &::before {
            content: '';
            display: block;
            padding-top: 100%;
        }

        position: relative;
        max-width: 100%;

        // Кадр ещё едет: плитка держит место, но не притворяется картинкой и не ловит
        // нажатия — открывать во весь экран пока нечего.
        &_pending {
            cursor: default;
        }

        // ГЛАВНОЕ ФОТО. Рамка цветом, а не одна метка: плитку опознают боковым зрением,
        // и в сетке из двадцати кадров искать глазами надпись — работа. Обводка снаружи
        // (box-shadow), а не толщина border: та сдвинула бы содержимое плитки на точку,
        // и соседние кадры в сетке перестали бы стоять ровно.
        &_primary {
            border-color: $green;
            box-shadow: 0 0 0 2px rgba(0, 176, 116, 0.25);
        }
    }

    // Метка поверх кадра, понизу во всю ширину: угловая пилюля на квадратной плитке
    // в 104 точки закрывает угол снимка, а нижняя полоса — небо или стол.
    &__photo-badge {
        position: absolute;
        right: 0;
        bottom: 0;
        left: 0;
        padding: 3px 4px;
        background: rgba(0, 176, 116, 0.92);
        color: #fff;
        font-size: 0.625rem;
        line-height: 0.875rem;
        text-align: center;
        // Плитка бывает узкой: лучше обрезать метку, чем разорвать её на две строки
        // и закрыть половину кадра.
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    &__photo-image {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
    }

    &__photo-actions,
    &__document-actions {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
        margin-top: 4px;
    }

    &__photo-actions {
        justify-content: space-between;
    }

    &__list {
        margin: 0;
        padding: 0;
        list-style: none;
    }

    &__document {
        padding: 10px 0;
        border-bottom: 1px solid var(--files-line);

        &:last-child {
            border-bottom: 0;
        }
    }

    // Кнопка, а не ссылка (открытого адреса у файла нет), поэтому браузерное оформление
    // кнопки снимается до нуля: строка документа должна выглядеть строкой, а не кнопкой.
    &__document-link {
        display: flex;
        width: 100%;
        align-items: center;
        gap: 10px;
        padding: 0;
        border: 0;
        background: transparent;
        color: inherit;
        font: inherit;
        text-align: left;
        text-decoration: none;
        cursor: pointer;

        &:disabled {
            cursor: default;
            opacity: 0.6;
        }
    }

    &__document-badge {
        flex: 0 0 auto;
        display: flex;
        width: 36px;
        height: 36px;
        align-items: center;
        justify-content: center;
        border-radius: 4px;
        background: rgba(44, 44, 44, 0.12);
        color: #f1905b;
        font: 400 0.6875rem var(--font-family);
        text-transform: uppercase;
    }

    &__document-box {
        flex: 1 1 auto;
        min-width: 0;
    }

    &__document-name {
        display: block;
        overflow: hidden;
        font-size: 0.8125rem;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    &__document-size {
        display: block;
        color: var(--files-faint);
        font-size: 0.75rem;
    }

    // Категория — пилюля, а не строка «Категория: …»: подпись повторялась бы у каждого
    // вложения и занимала бы больше места, чем само значение.
    &__chip {
        display: inline-block;
        margin: 6px 0 0;
        padding: 2px 8px;
        border-radius: 10px;
        background: var(--files-surface);
        color: var(--files-muted);
        font-size: 0.6875rem;
    }

    &__comment {
        margin: 4px 0 0;
        color: #3c4043;
        font-size: 0.75rem;
        // Подпись к кадру бывает длинной, и обрывать её нельзя: ради неё вложение
        // и открывают.
        overflow-wrap: anywhere;
    }

    &__meta {
        margin: 2px 0 0;
        color: var(--files-faint);
        font-size: 0.6875rem;
    }

    &__action {
        padding: 0;
        border: 0;
        background: transparent;
        color: $green;
        font-size: 0.75rem;
        text-decoration: none;
        cursor: pointer;
        // Строчные ссылки-кнопки пальцем не нажать: полоса нажатия растягивается
        // вертикально, не раздвигая при этом раскладку.
        line-height: 24px;

        &:disabled {
            color: var(--files-faint);
            cursor: default;
        }

        &_danger {
            color: var(--color-red);
        }

        &_icon {
            display: inline-flex;
            width: 32px;
            height: 32px;
            flex: 0 0 32px;
            align-items: center;
            justify-content: center;
            padding: 0;
            border: 1px solid var(--files-line);
            border-radius: 6px;
            line-height: 1;

            // StarIcon библиотеки использует класс star-icon, который есть и у
            // другой звезды проекта (60×60 px). Ограничиваем именно иконки кнопок.
            .material-design-icon {
                width: 18px;
                height: 18px;
                flex: 0 0 18px;
                font-size: 18px;
            }

            > .material-design-icon > .material-design-icon__svg {
                position: static;
                display: block;
                width: 100%;
                height: 100%;
            }

            &:hover,
            &:focus-visible {
                background: var(--files-surface);
            }
        }

        // «Сделать главным» — не ссылка, а кнопка, и выглядит кнопкой: у строчного
        // текста в 12 точек нет площади, за которую можно уверенно взяться пальцем.
        // 32 точки по высоте плюс поля по бокам дают такую площадь, не раздвигая плитку.
        //
        // ВИДНА ВСЕГДА, а не по наведению: на телефоне наводить нечем, и действие,
        // спрятанное в :hover, там просто не существует.
        &_primary {
            min-height: 32px;

            &:disabled {
                border-color: var(--files-line);
            }
        }

        // Признак уже стоит — действие снимает его, и кнопка это показывает: заливка
        // читается как «включено», а надпись меняется на «Снять главное».
        &_primary-on {
            border-color: $green;
            background: rgba(0, 176, 116, 0.1);
        }
    }
}
</style>
